import React, { useState } from "react";
import * as Yup from "yup";
import {
  BeadCrumb,
  Typography,
  Button,
  OnboardingDetails,
  Modal,
  Select,
  FormError,
  TextField,
} from "components";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, ErrorMessage } from "formik";
import { IoMdClose } from "react-icons/io";
import { ROUTES } from "routes/routePaths";
import makeRequest from "services/makeRequest";
import { useSelector } from "react-redux";
import { routes } from "routes/apiRoutes";
import { isSuccessResponse, setAlert, isEvenNumber } from "utils/helper";
import successImg from "assets/images/success-image.png";
import rejectedImg from "assets/images/rejected-account-img.png";
import { defaultValidation } from "utils/validators";

const AgentsKYCView = () => {
  const [modalIndex, setModalIndex] = useState(null);

  const { uploadedDocs } = useSelector((state) => state.onBoardingInfo);

  const location = useLocation();
  const navigate = useNavigate();

  const { agentId, comment } = location?.state;

  const rejectionValidationSchema = Yup.object({
    onboardingState: defaultValidation("Reason"),
  });

  const formatUploadedDocs = () => {
    let newUploadedDocs = { ...uploadedDocs };

    if (
      uploadedDocs.hasOwnProperty("validGuarantorId") ||
      uploadedDocs.hasOwnProperty("validGuarantorUploadedForm")
    ) {
      const { validGuarantorId, validGuarantorUploadedForm, id } =
        newUploadedDocs;
      let guarantorDetails = {};
      guarantorDetails.id = id;

      if (uploadedDocs.hasOwnProperty("validGuarantorId")) {
        guarantorDetails.validId = validGuarantorId;
        delete newUploadedDocs.validGuarantorId;
      }

      if (uploadedDocs.hasOwnProperty("validGuarantorUploadedForm")) {
        guarantorDetails.validForm = validGuarantorUploadedForm;
        delete newUploadedDocs.validGuarantorUploadedForm;
      }

      delete newUploadedDocs.id;

      newUploadedDocs.guarantorDetails = guarantorDetails;
    }
    return newUploadedDocs;
  };

  const updateKYCStatus = async (values) => {
    try {
      const newValues = { ...values, ...formatUploadedDocs() };

      // return console.log(JSON.stringify(newValues, null, 2))

      const { data } = await makeRequest(
        "put",
        routes.updateKYCStatus(agentId),
        newValues
      );
      if (isSuccessResponse(data)) {
        setModalIndex(modalIndex + 1);
      } else {
        setAlert(data.responseMessage);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getAccountValidateContent = (
    <div className="text-center ">
      <Typography variant="subtitle3" state="primary">
        Confirmation
      </Typography>
      <Typography className="my-4" variant="body1" state="primary">
        Are you sure you want to approve this KYC details?
      </Typography>
      <Formik
        initialValues={{ onboardingState: 9, status: null }}
        onSubmit={updateKYCStatus}
      >
        {({ isSubmitting }) => (
          <Form noValidate autoComplete="off">
            <div className="d-flex justify-content-center">
              <Button
                size="xs"
                label="Cancel"
                variant="secondary"
                disabled={isSubmitting}
                onClick={onClose}
              />
              <Button
                size="xs"
                label="Approve"
                style={{ marginLeft: "1rem" }}
                loading={isSubmitting}
                type="submit"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );

  const getAccountValidateSuccess = (
    <div className="text-center mt-2 mx-auto" style={{ width: "90%" }}>
      <img src={successImg} alt="" width="120" />
      <div style={{ marginTop: "2rem" }}>
        <Typography variant="body1" state="primary" className="mt-3 mb-4">
          Agent's KYC successfully validated and now awaits approval.
        </Typography>
      </div>
    </div>
  );

  const getAccountRejectContent = (
    <>
      <Typography variant="body0" className="text-center mb-4" state="primary">
        Are you sure you want to reject this account?
      </Typography>
      <Formik
        initialValues={{ onboardingState: "", comment, status: null }}
        onSubmit={updateKYCStatus}
        validationSchema={rejectionValidationSchema}
      >
        {({ isSubmitting, getFieldProps, errors, touched }) => (
          <Form noValidate autoComplete="off">
            <Select
              label="Select Reason for rejection"
              placeholder="Select reason"
              options={[
                { label: "Error on personal information", value: 2 },
                { label: "Error on Next of Kin Information", value: 4 },
                { label: "Error on Guarantor Forms", value: 6 },
                { label: "Error on files uploaded", value: 8 },
                { label: "Unable to verify KYC", value: 10 },
              ]}
              name="onboardingState"
              {...getFieldProps("onboardingState")}
              hasError={errors.onboardingState && touched.onboardingState}
            />
            <ErrorMessage component={FormError} name="onboardingState" />

            <TextField
              name="comment"
              type="text"
              placeholder="Input comment"
              label="Comment"
              containerClassName="mt-2"
              rows="5"
              required={false}
              {...getFieldProps("comment")}
            />

            <div className="d-flex justify-content-center mt-4">
              <Button
                size="xs"
                label="Cancel"
                variant="secondary"
                disabled={isSubmitting}
                onClick={onClose}
              />
              <Button
                size="xs"
                label="Decline"
                style={{ marginLeft: "1rem" }}
                loading={isSubmitting}
                type="submit"
                variant="danger"
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );

  const getAccountRejectSuccess = (
    <div className="text-center">
      <img src={rejectedImg} alt="Rejected" />
      <Typography variant="body1" state="primary" className="mt-3 mb-4">
        This account has been successfully rejected
      </Typography>
    </div>
  );

  const onClose = () => {
    if (isEvenNumber(modalIndex)) navigate(ROUTES.pendingKYCs);
    setModalIndex(null);
  };

  const disableApproval = () => {
    for (var key in uploadedDocs) {
      if (uploadedDocs[key] === false) return true;
    }
    return false;
  };

  const modalContentIndexes = [
    {
      index: 1,
      content: getAccountValidateContent,
    },
    {
      index: 2,
      content: getAccountValidateSuccess,
    },
    {
      index: 3,
      content: getAccountRejectContent,
    },
    {
      index: 4,
      content: getAccountRejectSuccess,
    },
  ];

  return (
    <div className="dashboard-page-container">
      <div className="d-flex justify-content-between align-items-center">
        <BeadCrumb
          titles={["Approvals", "Accounts", "Agent Awaiting KYC Verification"]}
        />
        <div>
          <Typography variant="caption1" className={"text-center"}>
            Account Status:
          </Typography>
          <Typography state="primary" variant="body2">
            Pending KYC Validation
          </Typography>
        </div>
      </div>
      <div style={{ marginTop: "3rem" }}>
        <Typography
          variant={"heading3"}
          state="primary"
          className={"text-center"}
        >
          New Account Awaiting Approval
        </Typography>

        <div className=" mt-4 d-flex justify-content-end align-items-center">
          <Typography variant="caption2">
            Reviewed all the provided details?
          </Typography>{" "}
          <Button
            label="Approve KYC Details"
            className={"mx-3 approval-custom-btn"}
            disabled={disableApproval()}
            onClick={() => {
              setModalIndex(1);
            }}
          />{" "}
          <Button
            variant="secondary"
            label="Reject KYC"
            size="sm"
            onClick={() => {
              setModalIndex(3);
            }}
          />
        </div>
      </div>
      <div style={{ marginTop: "3rem" }}>
        <OnboardingDetails allowDocumentValidation={true} />
      </div>

      <Modal showModal={!!modalIndex} onClose={onClose}>
        <div className="my-4">
          <div className="d-flex justify-content-end">
            <IoMdClose className="cursor-pointer" onClick={onClose} />
          </div>
          <div className="mt-2 mx-auto" style={{ width: "90%" }}>
            {modalContentIndexes[modalIndex - 1]?.content}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentsKYCView;
